<template>
  <div class="play-pause" :class="{ 'play-pause--gallery': isGallery }">
    <div class="columns play-pause__columns">
      <!-- D-92: `showIncrements` was DECLARED (:60, default true) and never read,
           so both skip columns rendered unconditionally and the caller's
           `:show-increments` was silently discarded. Mirrors v3's PlayPause,
           which gates both columns the same way. -->
      <div v-if="showIncrements" class="column play-pause__column">
        <button
          class="play-pause__button play-pause__operator play-pause__operator--decrement"
          :class="`play-pause__operator--${theme.type}`"
          ref="decrementButton"
          :disabled="isLoading"
          @click="decrement"
        >
          <span v-html="decrementSvg"></span>
          <span class="is-sr-only">Rewind 10 seconds</span>
        </button>
      </div>
      <div class="column play-pause__column">
        <div v-if="isLoading" class="loader-wrapper">
          <Loader :theme="theme"></Loader>
        </div>
        <button v-else class="play-pause__button" @click="togglePlay">
          <span v-html="playPauseSvg"></span>
          <span class="is-sr-only">
            {{ action }}
          </span>
        </button>
      </div>
      <div v-if="showIncrements" class="play-pause__column">
        <button
          class="play-pause__button"
          :class="`play-pause__operator--${theme.type}`"
          ref="incrementButton"
          :disabled="isLoading"
          @click="increment"
        >
          <span v-html="incrementSvg" class="play-pause__svg play-pause__svg--mini"></span>
          <span class="is-sr-only">Fast forward 30 seconds</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from './Loader';

export default {
  name: 'play-pause',
  emits: ['play', 'pause', 'decrement', 'increment'],
  components: {
    Loader,
  },
  props: {
    configs: {
      type: Object,
    },
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    showIncrements: {
      type: Boolean,
      default() {
        return true;
      },
    },
    isPlaying: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isGallery: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isLoading: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  computed: {
    action() {
      return this.isPlaying ? 'Pause' : 'Play';
    },

    playPauseSvg() {
      return this.isPlaying
        ? `
          <svg style="transform: scale(1.2)" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 0.5C8.33475 0.5 0.5 8.33475 0.5 18C0.5 27.6652 8.33475 35.5 18 35.5C27.6652 35.5 35.5 27.6652
            35.5 18C35.5 8.33475 27.6652 0.5 18 0.5ZM14.5 25C13.534 25 12.75 24.216 12.75 23.25V12.75C12.75 11.784
            13.534 11 14.5 11C15.466 11 16.25 11.784 16.25 12.75V23.25C16.25 24.216 15.466 25 14.5 25ZM21.5 25C20.534 25
            19.75 24.216 19.75 23.25V12.75C19.75 11.784 20.534 11 21.5 11C22.466 11 23.25 11.784 23.25 12.75V23.25C23.25
            24.216 22.466 25 21.5 25Z"
            fill="${this.configs.play_button || '#fff'}"
          /></svg>`
        : `
          <svg  style="transform: scale(1.2)" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 0.5C8.33475 0.5 0.5 8.33475 0.5 18C0.5 27.6652 8.33475 35.5 18 35.5C27.6652 35.5 35.5 27.6652
            35.5 18C35.5 8.33475 27.6652 0.5 18 0.5ZM14.5 24.125V11.875C14.5 11.154 15.3225 10.7428 15.9 11.175L24.0672
            17.3C24.5345 17.65 24.5345 18.35 24.0672 18.7L15.9 24.825C15.3225 25.2572 14.5 24.846 14.5 24.125Z"
            fill="${this.configs.play_button || '#fff'}"
          /></svg>`;
    },

    decrementSvg() {
      return `
        <svg style="transform: scale(1.1)" width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.98999 4V0L3.98999 5L8.98999 10V6C12.3 6 14.99 8.69 14.99 12C14.99 15.31 12.3 18 8.98999 18C5.67999 18
            2.98999 15.31 2.98999 12H0.98999C0.98999 16.42 4.56999 20 8.98999 20C13.41 20 16.99 16.42 16.99 12C16.99
            7.58 13.41 4 8.98999 4Z"
            fill="${this.configs.play_button || '#fff'}"
          />
          <path d="M7.89003 15H7.04003V11.74L6.03003 12.05V11.36L7.80003 10.73H7.89003V15Z" fill="${this.configs.play_button || '#fff'}" />
          <path
            d="M12.17 13.24C12.17 13.56 12.14 13.84 12.07 14.06C12 14.28 11.9 14.48 11.78 14.63C11.66 14.78 11.5 14.89
            11.33 14.96C11.16 15.03 10.96 15.06 10.74 15.06C10.52 15.06 10.33 15.03 10.15 14.96C9.97003 14.89 9.82003
            14.78 9.69003 14.63C9.56003 14.48 9.46003 14.29 9.39003 14.06C9.32003 13.83 9.28003 13.56 9.28003
            13.24V12.5C9.28003 12.18 9.31003 11.9 9.38003 11.68C9.45003 11.46 9.55003 11.26 9.67003 11.11C9.79003 10.96
            9.95003 10.85 10.12 10.78C10.29 10.71 10.49 10.68 10.71 10.68C10.93 10.68 11.12 10.71 11.3 10.78C11.48 10.85
            11.63 10.96 11.76 11.11C11.89 11.26 11.99 11.45 12.06 11.68C12.13 11.91 12.17 12.18 12.17 12.5V13.24ZM11.32
            12.38C11.32 12.19 11.31 12.03 11.28 11.9C11.25 11.77 11.21 11.67 11.16 11.59C11.11 11.51 11.05 11.45 10.97
            11.42C10.89 11.39 10.81 11.37 10.72 11.37C10.63 11.37 10.54 11.39 10.47 11.42C10.4 11.45 10.33 11.51 10.28
            11.59C10.23 11.67 10.19 11.77 10.16 11.9C10.13 12.03 10.12 12.19 10.12 12.38V13.35C10.12 13.54 10.13 13.7
            10.16 13.83C10.19 13.96 10.23 14.07 10.28 14.15C10.33 14.23 10.39 14.29 10.47 14.32C10.55 14.35 10.63 14.37
            10.72 14.37C10.81 14.37 10.9 14.35 10.97 14.32C11.04 14.29 11.11 14.23 11.16 14.15C11.21 14.07 11.25 13.96
            11.27 13.83C11.29 13.7 11.31 13.54 11.31 13.35V12.38H11.32Z"
            fill="${this.configs.play_button || '#fff'}"
          />
        </svg>`;
    },

    incrementSvg() {
      return `
        <svg style="transform: scale(1.1)" width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14 12C14 15.31 11.31 18 8 18C4.69 18 2 15.31 2 12C2 8.69 4.69 6 8 6V10L13 5L8 0V4C3.58 4 0 7.58 0 12C0
            16.42 3.58 20 8 20C12.42 20 16 16.42 16 12H14Z"
            fill="${this.configs.play_button || '#fff'}"
          />
          <path
            d="M6.06009 14.38C5.77009 14.38 5.44009 14.21 5.44009 13.84H4.59009C4.59009 14.81 5.49009 15.07 6.04009
            15.07C6.91009 15.07 7.55009 14.61 7.55009 13.82C7.55009 13.16 7.10009 12.92 6.84009 12.82C6.95009 12.77
            7.49009 12.5 7.49009 11.9C7.49009 11.69 7.44009 10.68 6.05009 10.68C5.43009 10.68 4.65009 11.03 4.65009
            11.84H5.50009C5.50009 11.5 5.81009 11.36 6.07009 11.36C6.66009 11.36 6.65009 11.86 6.65009 11.9C6.65009
            12.42 6.24009 12.49 6.02009 12.49H5.56009V13.15H6.01009C6.66009 13.15 6.71009 13.57 6.71009 13.79C6.71009
            14.11 6.50009 14.38 6.06009 14.38Z"
            fill="${this.configs.play_button || '#fff'}"
            />
          <path
            d="M9.84991 10.68C9.70991 10.68 8.40991 10.6 8.40991 12.5V13.24C8.40991 15.14 9.71991 15.06 9.84991
            15.06C9.98991 15.06 11.2899 15.15 11.2899 13.24V12.5C11.2999 10.59 9.98991 10.68 9.84991 10.68ZM10.4499
            13.35C10.4499 14.12 10.2399 14.38 9.85991 14.38C9.47991 14.38 9.25991 14.12 9.25991 13.35V12.38C9.25991
            11.63 9.47991 11.37 9.84991 11.37C10.2299 11.37 10.4499 11.63 10.4499 12.38V13.35Z"
            fill="${this.configs.play_button || '#fff'}"
            />
        </svg>`;
    },

    fill() {
      let fill;

      switch (this.theme.type) {
        case 'dark':
          fill = '#F5F5F5';
          break;

        case 'light':
          fill = '#a8a8ab';
          break;

        default:
          fill = this.theme.text;
      }

      return fill;
    },
  },
  methods: {
    togglePlay() {
      const action = this.isPlaying ? 'pause' : 'play';
      this.$emit(action);
    },

    decrement() {
      // isLoading already swaps the PLAY button for a Loader, but both skip columns
      // sit outside that v-if/v-else pair. `disabled` stops a real click; this stops
      // a synthetic one, which fires the handler regardless of the attribute (D-98).
      if (this.isLoading) return;
      const button = this.$refs.decrementButton;
      button.classList.add('counter-spin-move');
      setTimeout(() => {
        button.classList.remove('counter-spin-move');
      }, 200);

      this.$emit('decrement', 10);
    },

    increment() {
      // isLoading already swaps the PLAY button for a Loader, but both skip columns
      // sit outside that v-if/v-else pair. `disabled` stops a real click; this stops
      // a synthetic one, which fires the handler regardless of the attribute (D-98).
      if (this.isLoading) return;
      const button = this.$refs.incrementButton;
      button.classList.add('spin-move');
      setTimeout(() => {
        button.classList.remove('spin-move');
      }, 200);

      this.$emit('increment', 30);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/_helpers';

.play-pause {
  padding: 1.1rem 0;
  &--gallery {
    padding: 0;
  }
  &__columns {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 -0.75rem;
  }
  &__column {
    flex: 0 0 auto;
    padding: 0 0.4rem;
  }
  &__button {
    display: block;
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background: transparent;
    border: 0;
    position: relative;
    padding: 0;
    cursor: pointer;
    // D-98 review fold: while the element is reloading, both skip buttons are
    // `disabled` and their handlers return early — so a tap did NOTHING and said
    // nothing, not even the spin animation the tap normally gives back. A silent
    // no-op reads as a broken widget, so give the state an affordance. `:disabled`
    // only matches inside the load window, and no visual cell captures that state
    // (no visual cell captures the loading state at all — the `play` cells
    // screenshot after `canplay` — so nothing renders a disabled control), so this
    // moves no
    // golden.
    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
      &:hover {
        opacity: 0.45;
      }
    }
    &:hover {
      opacity: 0.8;
    }

    // R11 (a11y): the transport buttons render 35x35 on a phone, under the 44px
    // touch-target figure in the plan (§7.4). Expand the HIT AREA only — this
    // pseudo is transparent and absolutely positioned, so it is out of flow and
    // the row is pixel-identical; growing the button itself would re-space every
    // transport row and need a look sign-off. The columns are 47.8px apart
    // centre-to-centre (35px + 2 x 0.4rem padding), so 44px boxes cannot overlap
    // and steal a neighbour's tap. @media(max-width)-gated ONLY.
    @media (max-width: 480px) {
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 44px;
        height: 44px;
      }
    }
  }
  &__svg {
    display: block;
    width: 36px;
    height: 36px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    &--mini {
      width: 16px;
      height: 20px;
      margin-top: -2px;
    }
  }
  &__operator {
    transition: transform 0.1s ease-in;
    transform-origin: center center;
    margin-top: 2px;
    &--light {
      &:hover,
      &:focus:not(:active) {
        transform: scale(1.1);
      }
    }
    &--dark {
      &:hover,
      &:focus:not(:active) {
        background-color: rgba(255, 255, 255, 0.1);
        mix-blend-mode: screen;
      }
    }
  }
}

.counter-spin-move {
  animation: counterSpin 0.2s ease-in;
  opacity: 1;
}

.spin-move {
  animation: spin 0.2s ease-in;
  opacity: 1;
}

@keyframes counterSpin {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(-360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loader-wrapper {
  display: flex;
  width: 35px;
  height: 35px;
  justify-content: center;
  align-items: center;
}
</style>
